import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

import { API_URL } from '../config/api';

const EmployeeInvite = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        designation: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [designations, setDesignations] = useState([]);
    const [loadingDesignations, setLoadingDesignations] = useState(false);

    // Fetch designations (same API pattern as EmployeeAdd.js)
    useEffect(() => {
        fetchDesignations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDesignations = async () => {
        if (!API_URL) {
            return;
        }

        setLoadingDesignations(true);
        try {
            const payload = {
                action: 'get-designations',
                page: 1,
                limit: 50,
                status: 'active'
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success && result?.data) {
                setDesignations(result.data || []);
            } else {
                console.error('Failed to fetch designations:', result?.message);
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
            // Silent fail; no toast to avoid noise on load
        } finally {
            setLoadingDesignations(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email.trim())) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Designation is optional for invite; no validation needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: "invite-employee",
                name: formData.name.trim(),
                email: formData.email.trim(),
                ...(formData.designation && { designation: formData.designation, designationId: formData.designation })
            };  

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                toast.success(result.message || 'Invitation email sent successfully');
                // Reset form
                setFormData({
                    name: '',
                    email: ''
                });
                setErrors({});
            } else {
                // Handle different error scenarios
                const errorMessage = result?.message || 'Failed to send invitation';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error sending invitation:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    {/* Header with Title */}
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-12">
                            <h1 className="employee-title">
                                Invite Employee
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Send an invitation email to a new employee to join the organization.
                            </p>
                        </div>
                    </div>

                    {/* Invite Form Card */}
                    <div className="card employee-content-card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <h4 className="section-title">Employee Invitation</h4>
                                    <p className="section-subtitle">
                                        Enter the employee's name and email address to send an invitation
                                    </p>
                                </div>

                                <div className="form-row-3">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Name <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter employee name"
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Email <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter email address"
                                        />
                                        {errors.email && (
                                            <div className="invalid-feedback">{errors.email}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Designation
                                        </label>
                                        <select
                                            name="designation"
                                            className="form-control"
                                            value={formData.designation}
                                            onChange={handleChange}
                                            disabled={loadingDesignations}
                                        >
                                            <option value="">
                                                {loadingDesignations ? 'Loading designations...' : 'Select Designation (optional)'}
                                            </option>
                                            {designations.map((d) => (
                                                <option key={d.id} value={d.id}>
                                                    {d.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                {/* <i className="fas fa-spinner fa-spin"></i> */}
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Send Invitation</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EmployeeInvite;
